import {
  PaymentInitializationSpec,
  ResultPaymentInitializationSpec,
} from '@spa/components/pages/order/model/payment/initialization/specification'
import { PaymentWithApiPayment } from '@spa/store/modules/orderConfirm/types'
import { Either, right } from 'fp-ts/lib/Either'
import { none, Option } from 'fp-ts/lib/Option'

export function startChain(first: PaymentInitializationSpec): ChainPaymentInitializationSpec {
  return ChainPaymentInitializationSpec.start(first)
}

class ChainPaymentInitializationSpec implements PaymentInitializationSpec {
  /**
   * 支払い方法初期化仕様オブジェクトの結合を開始する
   */
  static start(first: PaymentInitializationSpec): ChainPaymentInitializationSpec {
    return new ChainPaymentInitializationSpec([first])
  }

  private constructor(private readonly specs: PaymentInitializationSpec[]) {}

  build(): Promise<Either<Error, Option<PaymentWithApiPayment>>> {
    return this.specs.reduce<Promise<ResultPaymentInitializationSpec>>(
      (prev, next) => prev.then((prevResult) => {
        /*
         * map/foldで実装しようとすると、最後にbuildメソッドを実行するルートでは
         * Promise<Either<Error, Option<Promise<Either<Error, Option<Payment>>>>>>
         * が発生する.
         * Option.map 内部でbuildを実行すると、その戻り値は
         * Option<Promise<Either<Error, Option<Payment>>>
         * となるため.
         *
         * ネストされたOption/Either全てについて
         * promise.then(some)
         * promise.then(right)
         * などを適用すればmap/foldでも実装できる可能性はあるが、かえって煩雑になるだけなので
         * if / early return での実装で済ませている.
         *
         * ScalaのようにOptionT/EitherTを使えば実現できるかもしれないが、
         * 公式ドキュメントでも利用例が全く存在せず、今回は断念した
         * https://gcanti.github.io/fp-ts/modules/EitherT.ts.html
         * https://gcanti.github.io/fp-ts/modules/OptionT.ts.html
         */
        if (prevResult.isLeft()) {
          return prevResult
        }

        const maybePrevPayment = prevResult.value
        if (maybePrevPayment.isSome()) {
          return right(maybePrevPayment)
        }

        return next.build()
      }),
      Promise.resolve(right(none))
    )
  }

  /**
   * 直前の仕様では支払い方法を決定できなかった場合に、次に適用する仕様を指定する.
   *
   * 複数回指定を行った場合は、指定した順に仕様を適用する.
   */
  thenTry(next: PaymentInitializationSpec): ChainPaymentInitializationSpec {
    return new ChainPaymentInitializationSpec([
      ...this.specs,
      next,
    ])
  }
}
